import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe2, ShieldCheck, FileCheck2, BadgeCheck, Plane } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import GlobalConnectivityGlobe from '@/components/products/GlobalConnectivityGlobe';
import { Button } from '@/components/ui/button';
import { DASHBOARD_SIGNIN_URL } from '@/config/links';
import ProductFAQAccordion from '@/components/products/ProductFAQAccordion';

const destinations = [
  { code: 'us', name: 'United States', short: 'US' },
  { code: 'gb', name: 'United Kingdom', short: 'UK' },
  { code: 'de', name: 'Germany', short: 'DE' },
  { code: 'ca', name: 'Canada', short: 'CA' },
  { code: 'ae', name: 'United Arab Emirates', short: 'UAE' },
  { code: 'cn', name: 'China', short: 'CN' },
  { code: 'jp', name: 'Japan', short: 'JP' },
  { code: 'sg', name: 'Singapore', short: 'SG' },
  { code: 'my', name: 'Malaysia', short: 'MY' },
  { code: 'au', name: 'Australia', short: 'AU' }
];

const internationalFaqs = [
  { question: 'How many countries can I ship to?', answer: 'You can ship across major global markets with scalable international carrier coverage.' },
  { question: 'Does this support customs workflows?', answer: 'Yes, customs documentation and compliance steps are integrated into the shipping process.' },
  { question: 'How are duties and taxes handled?', answer: 'Duty and tax visibility helps you manage landed cost planning before dispatch.' },
  { question: 'Can customers track international shipments?', answer: 'Yes, tracking includes global milestone visibility from dispatch to delivery.' },
  { question: 'Who is this best suited for?', answer: 'D2C brands, SMB exporters, and enterprises expanding into international markets.' }
];

const ProductInternationalShipping = () => {
  const routeCardRef = useRef(null);
  const [isRouteCardVisible, setIsRouteCardVisible] = useState(false);

  useEffect(() => {
    const target = routeCardRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRouteCardVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="International Shipping Platform for Global eCommerce Growth"
        description="Grow globally with compliant cross-border shipping, customs support, and route-level international delivery visibility."
        keywords="international shipping platform, cross border ecommerce logistics, customs clearance, global delivery network"
        canonical="https://boxdlogistics.in/products/international-shipping"
      />
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-b from-blue-100/50 via-blue-50/60 to-white px-4 pb-24 pt-28 md:pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59, 130, 246,0.20),transparent_35%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_80%,rgba(59, 130, 246,0.18),transparent_35%)]" />
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex rounded-full border border-blue-200 bg-white px-4 py-1 text-sm font-semibold text-blue-900">
              BoxdX · For International Use
            </span>
            <h1 className="mt-4 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
              Go Global with Premium Cross-Border Shipping Infrastructure
            </h1>
            <p className="mt-5 max-w-xl text-lg text-gray-600">
              Launch and scale international deliveries with customs-ready workflows, compliant documentation, and route-level shipment visibility.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button className="bg-gradient-to-r from-blue-800 to-blue-900 text-white hover:from-blue-900 hover:to-blue-950" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
                Start Global Shipping <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-50" asChild>
                <Link to="/contact">Book Expert Call</Link>
              </Button>
            </div>
          </div>

          <div
            ref={routeCardRef}
            className={`route-visual-card rounded-3xl border border-gray-200 bg-white p-6 shadow-xl ${
              isRouteCardVisible ? 'route-visual-card--visible' : ''
            }`}
          >
            <h2 className="mb-5 text-lg font-semibold text-gray-900">Global Route Visualization</h2>
            <GlobalConnectivityGlobe />
            <p className="mt-3 text-xs text-gray-500">Live transit routes and international checkpoints</p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-b from-white via-blue-50/40 to-white px-4 py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59, 130, 246,0.12),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-900 shadow-sm">
              Live corridors
            </span>
            <h2 className="mt-4 text-3xl font-bold text-gray-900 md:text-4xl">Countries Served Across Key Markets</h2>
            <p className="mt-3 text-sm text-gray-600 md:text-base">
              Dedicated cross-border lanes from India, with live transit, multi-carrier coverage, and compliance-ready dispatch.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {destinations.map((destination, index) => (
              <motion.div
                key={destination.code}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-100/60 blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60" />

                <div className="relative flex items-center gap-3">
                  <div className="relative h-10 w-14 overflow-hidden rounded-md shadow-sm ring-1 ring-black/5">
                    <img
                      src={`https://flagcdn.com/w160/${destination.code}.png`}
                      srcSet={`https://flagcdn.com/w320/${destination.code}.png 2x`}
                      alt={`${destination.name} flag`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-800">Active corridor</p>
                    <h3 className="text-base font-semibold text-gray-900">{destination.name}</h3>
                  </div>
                </div>

                <div className="relative mt-6 h-14">
                  <svg viewBox="0 0 220 56" className="absolute inset-0 h-full w-full" aria-hidden="true" focusable="false">
                    <defs>
                      <linearGradient id={`lane-${destination.code}`} x1="0" x2="1" y1="0" y2="0">
                        <stop offset="0%" stopColor="#93c5fd" />
                        <stop offset="100%" stopColor="#1e40af" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M14 40 Q 110 -6 206 40"
                      fill="none"
                      stroke={`url(#lane-${destination.code})`}
                      strokeWidth="1.6"
                      strokeDasharray="4 5"
                      strokeLinecap="round"
                    />
                    <circle cx="14" cy="40" r="4.5" fill="#1e40af" />
                    <circle cx="14" cy="40" r="8" fill="#1e40af" fillOpacity="0.15" />
                    <circle cx="206" cy="40" r="4.5" fill="#1f2937" />
                    <circle cx="206" cy="40" r="8" fill="#1f2937" fillOpacity="0.12" />
                  </svg>
                  <motion.div
                    className="absolute top-0 text-blue-800"
                    style={{
                      offsetPath: 'path("M 14 40 Q 110 -6 206 40")',
                      offsetRotate: 'auto'
                    }}
                    animate={{ offsetDistance: ['0%', '100%'] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: index * 0.25 }}
                  >
                    <Plane className="h-4 w-4 -translate-x-1/2 -translate-y-1/2" />
                  </motion.div>
                  <span className="absolute left-0 top-12 text-[10px] font-semibold uppercase tracking-wider text-gray-500">IN</span>
                  <span className="absolute right-0 top-12 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{destination.short}</span>
                </div>

                <div className="relative mt-8 flex items-center gap-2 border-t border-gray-100 pt-4">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                  </span>
                  <p className="text-xs text-gray-600">Lane status: Live</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-3">
          {[
            { title: 'Customs Documentation', description: 'Prepare shipment paperwork with fewer manual errors.', icon: FileCheck2 },
            { title: 'Compliance-first Workflow', description: 'Align shipping execution with destination-market requirements.', icon: ShieldCheck },
            { title: 'Trusted Global Network', description: 'Dispatch through vetted international courier partners.', icon: Globe2 }
          ].map((item, index) => {
            const Icon = item.icon;
            const isAccent = index === 1;
            return (
              <div key={item.title} className="rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 p-6">
                <span className={`inline-flex rounded-lg p-2 ${isAccent ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-900'}`}><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-gradient-to-b from-blue-50/60 to-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">Cross-border Story: From Checkout to Customs to Delivery</h2>
          <div className="mt-12 grid gap-4 lg:grid-cols-5">
            {[
              ['1', 'Order Captured', 'Destination and product details validated'],
              ['2', 'Document Prepared', 'Invoices and customs paperwork generated'],
              ['3', 'Export Cleared', 'Shipment cleared and dispatched to route'],
              ['4', 'In-transit Tracking', 'Live updates across international milestones'],
              ['5', 'Delivered Globally', 'Final handover with proof of delivery']
            ].map(([step, title, desc]) => (
              <div key={title} className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">Step {step}</p>
                <h3 className="mt-2 text-base font-semibold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 pb-16">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-r from-blue-800 to-blue-900 px-8 py-12 text-center text-white">
          <BadgeCheck className="mx-auto h-8 w-8 text-blue-100" />
          <h2 className="mt-4 text-3xl font-bold md:text-4xl">Expand to New Markets with Global Confidence</h2>
          <p className="mx-auto mt-3 max-w-2xl text-blue-100">Reduce cross-border complexity with an international shipping stack designed for compliance and speed.</p>
          <Button className="mt-7 bg-white text-blue-900 hover:bg-blue-50" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
            Start Shipping Smarter Today <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <ProductFAQAccordion title="International Shipping FAQs" items={internationalFaqs} />

      <Footer />
    </div>
  );
};

export default ProductInternationalShipping;
